import { DataAggregationFunction, DataCurrencyDisplay } from 'src/app/services/api.services';
import { DataType } from './DataType';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { DataDisplayOptions } from './DataDisplayOptions';
import { DataFormat } from './DataFormat';

export enum DataAggregationResult {
  /** Specific value out of the input. Preserves display options. */
  InputValue,

  /** Aggregate of the input. Preserves display options. */
  InputAggregate,

  /** Count of the input. Strips display options. */
  CountOf,
}

export interface DataAggregationOptions {
  readonly name: string;
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly result: DataAggregationResult;
  readonly formatDataQueryExpr: (identifier: string) => string;
  readonly aggregationOfText?: string;
}

let nextDataAggregationId = 1;

export class DataAggregation {
  readonly id = nextDataAggregationId++;

  constructor(
    public readonly value: DataAggregationFunction,
    private readonly options: DataAggregationOptions) { }

  get name() { return this.options.name; }
  get icon() { return this.options.icon; }
  get title() { return this.options.title; }
  get description() { return this.options.description; }
  get result() { return this.options.result; }
  get aggregationOfText() { return this.options.aggregationOfText ?? this.title; }

  getResultDataType(dataType: DataType) {
    if (this.result == DataAggregationResult.InputValue)
      return dataType;

    if (this.result == DataAggregationResult.InputAggregate) {
      if (dataType == DataType.Decimal)
        return DataType.Decimal;

      return DataType.Number;
    }
    
    if (this.result == DataAggregationResult.CountOf)
      return DataType.Integer;

    throw new Error(`Could not decide on aggregation result data type.`, { cause: dataType });
  }

  getResultDataFormat(dataDisplay: DataFormat): DataFormat {
    if (this.result === DataAggregationResult.CountOf) {
      return {
        ...dataDisplay,
        ...DataFormat.countOfDefault
      };
    }

    return dataDisplay;
  }

  getResultDataDisplay(dataDisplay: DataDisplayOptions): DataDisplayOptions {
    if (this.result === DataAggregationResult.CountOf) {
      return {
        ...dataDisplay,
        ...DataDisplayOptions.countOfDefault
      };
    }

    return dataDisplay;
  }

  canUseFor(dataType: DataType) {
    if ([DataAggregationFunction.Sum,
      DataAggregationFunction.Avg,
      DataAggregationFunction.Median,
      DataAggregationFunction.Min,
      DataAggregationFunction.Max,
      DataAggregationFunction.StandardDeviation,
      DataAggregationFunction.Variance].includes(this.value)) {
      if (!dataType.isNumeric)
        return false;
    }
    
    return true;
  }

  valueOf() {
    return this.value;
  }

  toString() {
    return this.value;
  }

  formatAggregationOfText(valueName: string) {
    return `${this.aggregationOfText} of ${valueName}`;
  }

  formatDataQueryExpr(identifier: string) {
    return this.options.formatDataQueryExpr(identifier);
  }
}

export namespace DataAggregation {
  export const Avg = new DataAggregation(DataAggregationFunction.Avg, {
    name: 'avg',
    icon: ``,
    title: `Average`,
    description: `Average of the data points will be returned.`,
    result: DataAggregationResult.InputAggregate,
    formatDataQueryExpr: i => `AGGR_AVERAGE(${i})`,
  });

  export const Count = new DataAggregation(DataAggregationFunction.CountNonNull, {
    name: 'count',
    icon: ``,
    title: `Count`,
    description: `Data points will be counted.`,
    result: DataAggregationResult.CountOf,
    formatDataQueryExpr: i => `AGGR_COUNT(${i})`,
  });

  export const CountDistinct = new DataAggregation(DataAggregationFunction.Count, {
    name: 'distinctCount',
    icon: ``,
    title: `Count Distinct`,
    description: `Unique data points will be counted.`,
    result: DataAggregationResult.CountOf,
    formatDataQueryExpr: i => `AGGR_COUNT(${i}, {DISTINCT})`,
    aggregationOfText: `Distinct count`,
  });

  export const Max = new DataAggregation(DataAggregationFunction.Max, {
    name: 'max',
    icon: ``,
    title: `Maximum`,
    description: `Largest data point will be returned.`,
    result: DataAggregationResult.InputValue,
    formatDataQueryExpr: i => `AGGR_MAX(${i})`,
  });

  export const Median = new DataAggregation(DataAggregationFunction.Median, {
    name: 'median',
    icon: ``,
    title: `Median`,
    description: `Median of the data points will be returned.`,
    result: DataAggregationResult.InputAggregate,
    formatDataQueryExpr: i => `AGGR_MEDIAN(${i})`,
  });

  export const Min = new DataAggregation(DataAggregationFunction.Min, {
    name: 'min',
    icon: ``,
    title: `Minimum`,
    description: `Smallest data point will be returned.`,
    result: DataAggregationResult.InputValue,
    formatDataQueryExpr: i => `AGGR_MIN(${i})`,
  });

  export const StandardDeviation = new DataAggregation(DataAggregationFunction.StandardDeviation, {
    name: 'stdDev',
    icon: ``,
    title: `Standard Deviation`,
    description: `Standard Deviation of the data points will be returned.`,
    result: DataAggregationResult.InputAggregate,
    formatDataQueryExpr: i => `AGGR_STDDEV(${i})`,
    aggregationOfText: `Standard deviation`,
  });

  export const Sum = new DataAggregation(DataAggregationFunction.Sum, {
    name: 'sum',
    icon: ``,
    title: `Sum`,
    description: `Sum of the data points will be returned.`,
    result: DataAggregationResult.InputAggregate,
    formatDataQueryExpr: i => `AGGR_SUM(${i})`,
  });

  export const Variance = new DataAggregation(DataAggregationFunction.Variance, {
    name: 'var',
    icon: ``,
    title: `Variance`,
    description: `Variance of the data points will be returned.`,
    result: DataAggregationResult.InputAggregate,
    formatDataQueryExpr: i => `AGGR_VAR(${i})`,
  });

  export const list = [
    Sum,
    Avg,
    Max,
    Median,
    Min,
    
    StandardDeviation,
    Variance,

    Count,
    CountDistinct,
  ];

  export function from(value: DataAggregationFunction): DataAggregation | null {
    switch (value) {
      case DataAggregationFunction.DoNotSummarize: return null;
      case DataAggregationFunction.Avg: return Avg;
      case DataAggregationFunction.Count: return CountDistinct;
      case DataAggregationFunction.CountNonNull: return Count;
      case DataAggregationFunction.Max: return Max;
      case DataAggregationFunction.Median: return Median;
      case DataAggregationFunction.Min: return Min;
      case DataAggregationFunction.StandardDeviation: return StandardDeviation;
      case DataAggregationFunction.Sum: return Sum;
      case DataAggregationFunction.Variance: return Variance;
      default: null;
    }
  }
}
